import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  CopyButton,
  Field,
  Page,
  PageHeader,
  TextArea,
} from '@evogard/ui';
import {
  ARRIVAL_FAR_M,
  DISPATCH_PROGRESS_LABELS,
  memberTrackingMessage,
  type Tone,
} from '@evogard/domain';
import type {
  DispatchDetail,
  DispatchProgressSummary,
  DispatchStep,
  ProgressSettingsValue,
} from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { ASSISTANCE_KEY, km, minutes, when } from './assistance.js';

export const PROGRESS_SETTINGS_KEY = [...ASSISTANCE_KEY, 'prazos'] as const;

/** O relógio da tela: o "há 6 min" anda sozinho, sem esperar a próxima leitura. */
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(timer);
  }, [everyMs]);
  return now;
}

/** "agora", "há 6 min", "há 1 h 20". */
export function ago(iso: string | null, now: number): string {
  if (!iso) return '—';
  const min = Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  const rest = min % 60;
  return rest ? `há ${h} h ${rest}` : `há ${h} h`;
}

/** A cor do andamento: o que pede atenção grita; o resto fica quieto. */
function toneOf(p: DispatchProgressSummary): Tone {
  if (p.late || p.signalLost || p.plateLocked) return 'erro';
  if (p.progress === 'finalizado') return 'sucesso';
  if (p.progress === 'nao_abriu') return 'neutro';
  return 'informacao';
}

/** O andamento na linha da tabela de Acionamentos (`assistencia-30`). */
export function ProgressCell({ p, now }: { p: DispatchProgressSummary; now: number }) {
  const flags = [
    p.late && 'atrasado',
    p.plateLocked && 'placa travada',
    p.signalLost && `sem sinal ${ago(p.lastSignalAt, now)}`,
  ].filter(Boolean);
  return (
    <div className="flex flex-col items-start gap-1">
      <Badge tone={toneOf(p)}>{DISPATCH_PROGRESS_LABELS[p.progress]}</Badge>
      <span className="text-xs text-texto-tenue tabular-nums">
        {p.progress === 'finalizado' ? when(p.since) : ago(p.since, now)}
      </span>
      {flags.length > 0 && (
        <span className="text-xs font-semibold text-erro">{flags.join(' · ')}</span>
      )}
    </div>
  );
}

/** O que cada marco acrescenta à linha do tempo. */
export function stepDetail(s: DispatchStep): { text: string; far: boolean } {
  const far = s.distanceM !== null && s.distanceM > ARRIVAL_FAR_M;
  switch (s.kind) {
    case 'saiu':
      return {
        text: s.etaS !== null ? `previsão refeita: ${minutes(s.etaS)} até o local` : '',
        far: false,
      };
    case 'chegou':
      return {
        text:
          s.distanceM !== null
            ? `a ${km(s.distanceM)} do endereço cadastrado${far ? ' — longe do endereço' : ''}`
            : 'sem localização',
        far,
      };
    case 'chegou_destino':
      return {
        text:
          s.distanceM !== null
            ? `a ${km(s.distanceM)} do destino${far ? ' — longe do destino' : ''}`
            : 'sem localização',
        far,
      };
    case 'placa_errada':
      return { text: `digitou ${s.typedPlate ?? '—'}`, far: false };
    case 'placa_conferida':
      return {
        text: s.typedPlate ? `digitou ${s.typedPlate}` : 'veículo confirmado pelo modelo',
        far: false,
      };
    case 'placa_destravada':
      return { text: `${s.actorName ?? '—'}: ${s.reason ?? ''}`, far: false };
    default:
      return { text: '', far: false };
  }
}

/**
 * O andamento agora, no topo da aba do prestador (`assistencia-29` a `-34`):
 * a previsão de chegada, a última posição, a placa travada e o lembrete para
 * quando a posição para de chegar. Os marcos são a linha do tempo da aba
 * (`ProviderTimeline`, em `DispatchDeskTabs.tsx`).
 *
 * Não é o status: o pagamento continua sendo o do acionamento (decisão 2 de
 * 29/10/2026), e nada aqui muda o que se paga.
 */
export function ProgressPanel({
  d,
  onChanged,
}: {
  d: DispatchDetail;
  onChanged: (r: DispatchDetail) => void;
}) {
  const now = useNow();
  const p = d.progress;
  if (!p) return null;

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-borda p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold">Agora</h3>
        <Badge tone={toneOf(p)}>{DISPATCH_PROGRESS_LABELS[p.progress]}</Badge>
        {p.late && <Badge tone="erro">Atrasado</Badge>}
        {p.signalLost && <Badge tone="erro">Sem sinal {ago(p.lastSignalAt, now)}</Badge>}
        {p.plateLocked && <Badge tone="erro">Placa travada</Badge>}
      </div>

      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-3">
        <div>
          <dt className="text-texto-suave">Previsão do Google</dt>
          <dd className="tabular-nums">{when(p.arrival.googleAt)}</dd>
        </div>
        <div>
          <dt className="text-texto-suave">Prazo de chegada</dt>
          <dd className="tabular-nums">
            {p.arrival.deadline ? when(p.arrival.deadline) : 'sem prazo na base'}
          </dd>
        </div>
        <div>
          <dt className="text-texto-suave">Última posição</dt>
          <dd className="tabular-nums">
            {p.lastPosition
              ? `${ago(p.lastPosition.at, now)}${p.lastPosition.accuracyM !== null ? ` · ±${p.lastPosition.accuracyM} m` : ''}`
              : 'nenhuma ainda'}
          </dd>
        </div>
      </dl>

      {p.plateLocked && (
        <UnlockPlate d={d} canUnlock={p.canUnlockPlate} onChanged={onChanged} />
      )}

      {p.reminder && (p.signalLost || p.progress === 'nao_abriu') && (
        <Alert tone="alerta">
          <p>
            {p.progress === 'nao_abriu'
              ? 'O prestador ainda não abriu o link.'
              : 'A posição do prestador parou de chegar.'}{' '}
            Mande o lembrete no WhatsApp dele:
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <CopyButton text={p.reminder} label="Copiar lembrete" />
            {d.provider.whatsapp && (
              <a
                className="underline"
                href={`https://wa.me/55${d.provider.whatsapp}`}
                target="_blank"
                rel="noreferrer"
              >
                Abrir o WhatsApp do prestador
              </a>
            )}
          </div>
        </Alert>
      )}

      {d.memberLinkUrl && <MemberLink d={d} url={d.memberLinkUrl} />}
    </section>
  );
}

/**
 * O link do associado acompanhar o prestador até a chegada (30/10/2026). Quem
 * manda é a pessoa da central, como o script do prestador: não há modelo de
 * WhatsApp aprovado para a casa mandar sozinha.
 */
function MemberLink({ d, url }: { d: DispatchDetail; url: string }) {
  const message = memberTrackingMessage(d.code, url);
  const phone = d.callbackPhone.replace(/\D/g, '');
  return (
    <div className="flex flex-col gap-2 border-t border-borda pt-3">
      <p>
        <span className="font-semibold">Acompanhamento do associado.</span>{' '}
        <span className="text-texto-suave">
          Ele vê acionado, a caminho e chegou, com a previsão — nunca a posição do
          prestador — e o link não muda quando o do prestador é trocado.
        </span>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <CopyButton text={message} label="Copiar mensagem do associado" />
        {phone && (
          <a
            className="underline"
            href={`https://wa.me/55${phone}?text=${encodeURIComponent(message)}`}
            target="_blank"
            rel="noreferrer"
          >
            Abrir o WhatsApp do associado
          </a>
        )}
      </div>
    </div>
  );
}

/** Quem acionou destrava a placa, com o motivo (decisão 1 de 29/10/2026). */
function UnlockPlate({
  d,
  canUnlock,
  onChanged,
}: {
  d: DispatchDetail;
  canUnlock: boolean;
  onChanged: (r: DispatchDetail) => void;
}) {
  const [reason, setReason] = useState('');
  const unlock = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.unlockPlate({
          params: { id: d.id },
          body: { reason: reason.trim() },
        }),
        200,
        'Não foi possível destravar a placa.',
      ),
    onSuccess: (r) => {
      setReason('');
      onChanged(r);
    },
  });
  if (!canUnlock) {
    return (
      <Alert tone="alerta">
        O prestador errou a placa três vezes e está parado. Só quem acionou este serviço
        pode destravar.
      </Alert>
    );
  }
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-erro bg-erro-suave p-3">
      <p>
        O prestador errou a placa três vezes. Confira com ele por telefone e destrave
        dizendo o motivo.
      </p>
      {unlock.error && <Alert tone="erro">{unlock.error.message}</Alert>}
      <TextArea
        label="Motivo do desbloqueio"
        required
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div>
        <Button
          size="sm"
          disabled={reason.trim().length < 3 || unlock.isPending}
          loading={unlock.isPending}
          onClick={() => unlock.mutate()}
        >
          Destravar a placa
        </Button>
      </div>
    </div>
  );
}

/** Um campo de minutos ou porcentagem que pode ficar vazio. */
function asNumber(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : Number.NaN;
}

/**
 * Os prazos do acompanhamento da base (`assistencia-34`). Vazios, a tela mostra
 * a previsão do Google e não pinta nada de vermelho — quem escolhe os números
 * é a operação.
 */
export function ProgressSettingsScreen() {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: PROGRESS_SETTINGS_KEY,
    queryFn: async () =>
      ok(
        await api.assistanceDispatches.progressSettings(),
        200,
        'Não foi possível ler os prazos.',
      ),
  });
  const [form, setForm] = useState<Record<keyof ProgressSettingsValue, string> | null>(
    null,
  );
  const value =
    form ??
    (query.data
      ? {
          arrivalPrepMin: query.data.arrivalPrepMin?.toString() ?? '',
          arrivalSlackPct: query.data.arrivalSlackPct?.toString() ?? '',
          noSignalMin: query.data.noSignalMin?.toString() ?? '',
        }
      : null);
  const body = value
    ? {
        arrivalPrepMin: asNumber(value.arrivalPrepMin),
        arrivalSlackPct: asNumber(value.arrivalSlackPct),
        noSignalMin: asNumber(value.noSignalMin),
      }
    : null;
  const valid = body !== null && Object.values(body).every((n) => !Number.isNaN(n));
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.saveProgressSettings({ body: body! }),
        200,
        'Não foi possível gravar os prazos.',
      ),
    onSuccess: (r) => {
      client.setQueryData(PROGRESS_SETTINGS_KEY, r);
      void client.invalidateQueries({ queryKey: ASSISTANCE_KEY });
      setForm(null);
    },
  });
  const set = (key: keyof ProgressSettingsValue) => (e: { target: { value: string } }) =>
    value && setForm({ ...value, [key]: e.target.value });

  return (
    <Page measure="leitura">
      <PageHeader title="Prazos do acompanhamento">
        Vazios, a tabela de Acionamentos mostra a previsão do Google e não marca nada como
        atrasado.
      </PageHeader>
      {(query.error ?? save.error) && (
        <Alert tone="erro">{(query.error ?? save.error)!.message}</Alert>
      )}
      {save.isSuccess && !form && <Alert tone="sucesso">Prazos gravados.</Alert>}
      {value && (
        <Card>
          <div className="flex flex-col gap-3">
            <Field
              label="Preparo do prestador"
              suffix="min"
              inputMode="numeric"
              value={value.arrivalPrepMin}
              onChange={set('arrivalPrepMin')}
              help="Quanto ele leva para sair depois de acionado. Some à previsão do Google até ele marcar que saiu."
            />
            <Field
              label="Folga sobre o tempo do Google"
              suffix="%"
              inputMode="numeric"
              value={value.arrivalSlackPct}
              onChange={set('arrivalSlackPct')}
              help="50 é uma vez e meia o tempo do Google. O tempo da cotação não conta o trânsito."
            />
            <Field
              label="Sem sinal depois de"
              suffix="min"
              inputMode="numeric"
              value={value.noSignalMin}
              onChange={set('noSignalMin')}
              help="Com o serviço em curso, a linha fica vermelha quando a posição para de chegar por este tempo."
            />
            <div className="flex justify-end">
              <Button
                disabled={!valid || save.isPending}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                Gravar prazos
              </Button>
            </div>
          </div>
        </Card>
      )}
    </Page>
  );
}
